import store from '@ps/store'
import gameIframe from '@ps/bridge/iframe/iframe'
import { ElUploadInternalFileDetail } from 'element-ui/types/upload'
import { getBgmAudioUrl } from '@ps/workbench/global-config'
import validator, { ValidatorRule } from '@com/utils/file-validator'
import { getFileExtension } from '@com/utils/file'
import i18n from '@ps/lang'
import { maxAudioSize } from '@ps/utils/config'
import { libDataPool } from '@ps/bridge/resource/pool'

export function validate(file: File) {
  const audioTypes = ['mp3']
  const rules = [
    {
      validate: ({ file }: ValidatorRule) => {
        if (!audioTypes.includes(getFileExtension(file.name.toLowerCase()))) {
          return {
            valid: false,
            message: `${i18n.t(
              'workbench.resourceManagers.audioUploader.uploadRule[0]',
            )} ${audioTypes.join('、')} ${i18n.t(
              'workbench.resourceManagers.audioUploader.uploadRule[1]',
            )}`,
          }
        }
        if (file.size > maxAudioSize * 1024 * 1024) {
          return {
            valid: false,
            message: `${i18n.t(
              'workbench.resourceManagers.audioUploader.uploadRule[2]',
            )}${maxAudioSize}M`,
          }
        }
        return {
          valid: true,
        }
      },
      file: file,
    },
  ]
  return validator(rules)
}

/**
 * 添加音频到项目资源库下
 * @param file
 * @param category
 */
export async function addAudioToProjectStore({
  file,
  category,
}: {
  file: ElUploadInternalFileDetail
  category: string
}) {
  const fileName = file.name.toLowerCase()
  const { valid, errors } = validate(file.raw)
  if (!valid) {
    // @ts-ignore
    throw new Error(`${errors[0].message}`)
  }

  const res = await store.dispatch(
    'workspace/resourceManager/createComponent',
    {
      data: {
        file: file.raw,
        name: fileName, // 保存原始名字到远端，保证从资源库新增资源时能够使用原有的名字
      },
      category,
    },
  )

  if (res.code !== 0) {
    throw new Error(res.msg)
  }
  return res.data
}

/**
 * 添加音频到 playable 项目中
 */
export async function addAudioToPlayable({
  isAdd,
  category,
  fileUrl,
  fileId,
  fileName,
  from,
  isProjectOnly = false,
}: {
  isAdd: boolean
  category: string
  fileUrl: string
  fileId: number
  fileName: string
  from?: number
  isProjectOnly: boolean
}) {
  const isBgm = category === 'bgm'
  // 必须等到游戏加载后才会添加到游戏中
  if (!gameIframe.iframe || !gameIframe.isGameReady) {
    return false
  }

  // 背景音乐，暂时拖拽上传不会走这个逻辑
  if (isBgm) {
    // 添加资源到项目中
    const { path, file_size, duration } = await store.dispatch(
      'workspace/game/audio/addAudioResourceToProject',
      {
        asset_id: fileId,
        path: getBgmAudioUrl(),
        from,
      },
    )
    const data = {
      sourceAudioUrl: fileUrl,
      size: file_size,
      audioUrl: path,
      sourceAudioDuration: duration,
    }
    store.commit('workspace/game/bgm/setBgmBehConfig', data)
    store.commit('workspace/game/bgm/setBgmConfig', data)
    return true
  }

  // 音效
  if (!gameIframe.ps.AudioNode) {
    throw new Error('该模板版本不支持添加音效')
  }
  const { path, file_size, duration } = await store.dispatch(
    'workspace/game/audio/addAudioResourceToProject',
    {
      asset_id: fileId,
      from,
    },
  )
  // 记录到资源映射池，方便后续使用
  libDataPool.setLibData(path, {
    name: fileName,
    data: {
      file_url: fileUrl,
      path,
      file_size,
      duration,
    },
  })
  // 只添加资源到项目中，不添加到游戏中
  if (isProjectOnly) return true
  if (isAdd) {
    await store.dispatch('workspace/game/audio/addAudioNode', {
      file_url: fileUrl,
      file_size,
      path,
      name: fileName,
      duration,
    })
  } else {
    await store.dispatch('workspace/game/audio/toggleAudioNode', {
      file_url: fileUrl,
      file_size,
      path,
      name: fileName,
      duration,
    })
  }
  return true
}
